import * as THREE from 'three';
import { QuaternionSchema, Vec3Schema } from '@/data/common';
import z from 'zod';

export const SceneObjectSchema = z.object({
  position: Vec3Schema.default(new THREE.Vector3(0, 0, 0)),
  scale: Vec3Schema.default(new THREE.Vector3(1, 1, 1)),
  quaternion: QuaternionSchema.default(new THREE.Quaternion(0, 0, 0, 1)),
  offset: z
    .object({
      x: z.number().default(0),
      y: z.number().default(0),
      z: z.number().default(0),
    })
    .default({ x: 0, y: 0, z: 0 })
    .transform(obj => new THREE.Vector3(obj.x, obj.y, obj.z)),
});

type SceneObjectKey<SchemaType extends typeof SceneObjectSchema> = Extract<
  keyof z.infer<SchemaType>,
  string
>;
type SceneObjectListener<
  SchemaType extends typeof SceneObjectSchema,
  K extends SceneObjectKey<SchemaType> | 'position' | 'scale' | 'quaternion',
> = (value: z.infer<SchemaType>[K]) => void;

export abstract class SceneObject<
  SchemaType extends typeof SceneObjectSchema = typeof SceneObjectSchema,
  SchemaKeys extends SceneObjectKey<SchemaType> = SceneObjectKey<SchemaType>,
> {
  static schema = SceneObjectSchema;

  protected readonly outerObject = new THREE.Group();
  protected readonly innerObject = new THREE.Group();
  private mountedScene: THREE.Scene | null = null;
  private readonly listeners = new Map<
    SceneObjectKey<SchemaType>,
    Set<(value: unknown) => void>
  >();
  private readonly anyListeners = new Set<
    (key: string, value: unknown) => void
  >();
  protected data: z.infer<SchemaType>;

  get schema(): SchemaType {
    return (this.constructor as typeof SceneObject).schema as SchemaType;
  }

  constructor(options: z.input<typeof SceneObjectSchema>) {
    const opts = this.schema.parse(options);
    this.position.copy(opts.position);
    this.scale.copy(opts.scale);
    this.quaternion.copy(opts.quaternion);
    this.data = opts;

    this.data.position = this.position;
    this.data.scale = this.scale;
    this.data.quaternion = this.quaternion;

    this.outerObject.add(this.innerObject);
    this.innerObject.position.copy(opts.offset);
  }

  get position(): THREE.Vector3 {
    return this.outerObject.position;
  }

  get scale(): THREE.Vector3 {
    return this.outerObject.scale;
  }

  get quaternion(): THREE.Quaternion {
    return this.outerObject.quaternion;
  }

  set position(
    x: { x: number; y: number; z: number } | [number, number, number],
  ) {
    if (Array.isArray(x)) {
      this.outerObject.position.set(x[0], x[1], x[2]);
    } else {
      this.outerObject.position.set(x.x, x.y, x.z);
    }

    this.emit('position', this.position);
  }

  set scale(x: { x: number; y: number; z: number } | [number, number, number]) {
    if (Array.isArray(x)) {
      this.outerObject.scale.set(x[0], x[1], x[2]);
    } else {
      this.outerObject.scale.set(x.x, x.y, x.z);
    }

    this.emit('scale', this.scale);
  }

  set quaternion(
    x:
      | { x: number; y: number; z: number; w: number }
      | [number, number, number, number],
  ) {
    if (Array.isArray(x)) {
      this.outerObject.quaternion.set(x[0], x[1], x[2], x[3]);
    } else {
      this.outerObject.quaternion.set(x.x, x.y, x.z, x.w);
    }

    this.emit('quaternion', this.quaternion);
  }

  setPosition(x: number, y: number, z: number) {
    this.outerObject.position.set(x, y, z);
    this.emit('position', this.position);
  }

  setScale(x: number, y: number, z: number) {
    this.outerObject.scale.set(x, y, z);
    this.emit('scale', this.scale);
  }

  setQuaternion(x: number, y: number, z: number, w: number) {
    this.outerObject.quaternion.set(x, y, z, w);
    this.emit('quaternion', this.quaternion);
  }

  setRotationFromEuler(
    x: number,
    y: number,
    z: number,
    order: THREE.EulerOrder = 'XYZ',
  ) {
    this.outerObject.quaternion.setFromEuler(new THREE.Euler(x, y, z, order));
    this.emit('quaternion', this.quaternion);
  }

  on<K extends SchemaKeys | 'position' | 'scale' | 'quaternion'>(
    key: K,
    listener: SceneObjectListener<SchemaType, K>,
  ): () => void;
  on<K extends SchemaKeys>(
    key: K,
    listener: SceneObjectListener<SchemaType, K>,
  ) {
    const set =
      this.listeners.get(key) ?? new Set<SceneObjectListener<SchemaType, K>>();
    set.add(listener);
    this.listeners.set(key, set as Set<(value: unknown) => void>);

    return () => {
      set.delete(listener);
      if (set.size === 0) {
        this.listeners.delete(key);
      }
    };
  }

  onAny(listener: (key: string, value: unknown) => void) {
    this.anyListeners.add(listener);

    return () => {
      this.anyListeners.delete(listener);
    };
  }

  protected emit<K extends SchemaKeys | 'position' | 'scale' | 'quaternion'>(
    key: K,
    value: z.infer<SchemaType>[K],
  ): void;
  protected emit<K extends SchemaKeys>(key: K, value: z.infer<SchemaType>[K]) {
    for (const listener of this.anyListeners) {
      listener(key, value);
    }

    const set = this.listeners.get(key);
    if (!set) {
      return;
    }

    for (const listener of set) {
      listener(value);
    }
  }

  set<K extends SchemaKeys>(key: K, value: z.infer<SchemaType>[K]) {
    if (!(key in this.data)) {
      throw new Error(`Key ${key} does not exist in data`);
    }

    if (key in this) {
      // @ts-expect-error - We already checked that the key exists in this.data and in this
      this[key] = value as any;
    } else {
      this.data[key] = value;
    }

    this.emit(key, value);
  }

  get<K extends SchemaKeys>(key: K): z.infer<SchemaType>[K] {
    if (!(key in this.data)) {
      throw new Error(`Key ${key} does not exist in data`);
    }

    return this.data[key];
  }

  getData(): z.infer<SchemaType> {
    return this.data;
  }

  render(scene: THREE.Scene): void {
    if (this.mountedScene === scene) {
      return;
    }

    this.mountedScene = scene;
    this.renderObject(this.innerObject);
    scene.add(this.outerObject);
  }

  protected abstract renderObject(parent: THREE.Object3D): void;

  getPickTargets(): THREE.Object3D[] {
    return [];
  }

  onClick(_intersection: THREE.Intersection, _event: PointerEvent): void {}

  update(_delta: number): void {}

  dispose(): void {
    this.listeners.clear();
    this.outerObject.removeFromParent();
    this.mountedScene = null;
  }
}
